import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import {
  todayYMD,
  addDays,
  prettyDate,
  fromYMD,
  DOW_LABELS,
  buildWeeklyTeasingReportHtml,
  printHtml,
  DEFAULT_SETTINGS,
} from "@/lib/equi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { ChevronLeft, ChevronRight, Printer, Plus, Trash2, Loader2 } from "lucide-react";

export default function WeeklyTeasing() {
  const { readOnly } = useAuth();
  const [anchor, setAnchor] = useState(todayYMD());
  const [week, setWeek] = useState(null);
  const [mares, setMares] = useState([]);
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // {mare_id, mare_name, date, result, notes}
  const [saving, setSaving] = useState(false);
  const [addMareId, setAddMareId] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [w, m, s] = await Promise.all([
        api.get(`/teasing/week/${anchor}`),
        api.get("/mares"),
        api.get("/settings"),
      ]);
      setWeek(w.data);
      setMares(m.data);
      setSettings(s.data);
    } catch {
      toast.error("Could not load teasing week");
    } finally {
      setLoading(false);
    }
  }, [anchor]);

  useEffect(() => {
    load();
  }, [load]);

  const rosterIds = useMemo(() => new Set((week?.rows || []).map((r) => r.mare_id)), [week]);
  const available = mares.filter((m) => !rosterIds.has(m.id));

  const addMare = async () => {
    if (!addMareId) return;
    try {
      await api.post(`/teasing/week/${anchor}/mare/${addMareId}`);
      setAddMareId("");
      load();
    } catch {
      toast.error("Failed to add mare");
    }
  };

  const removeMare = async (mareId) => {
    try {
      await api.delete(`/teasing/week/${anchor}/mare/${mareId}`);
      load();
    } catch {
      toast.error("Failed to remove mare");
    }
  };

  const openCell = (row, date) => {
    const cell = row.days[date] || { result: "", notes: "" };
    setEditing({ mare_id: row.mare_id, mare_name: row.mare_name, date, result: cell.result || "", notes: cell.notes || "" });
  };

  const saveCell = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      await api.put("/teasing", {
        mare_id: editing.mare_id,
        date: editing.date,
        result: editing.result || "",
        notes: editing.notes || "",
      });
      toast.success("Saved");
      setEditing(null);
      load();
    } catch {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handlePrint = () => {
    if (week) printHtml(buildWeeklyTeasingReportHtml(week, settings));
  };

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight">Weekly Teasing</h1>
          <p className="text-muted-foreground text-sm">Track teasing results across the week.</p>
        </div>
        <Button size="sm" onClick={handlePrint} data-testid="teasing-print-btn" className="gap-2">
          <Printer className="h-4 w-4" /> Print
        </Button>
      </div>

      {/* Week navigator */}
      <div className="flex items-center justify-between gap-3 bg-card border border-border rounded-xl p-3 mb-5">
        <Button variant="ghost" size="icon" onClick={() => setAnchor(addDays(anchor, -7))} data-testid="teasing-prev-week">
          <ChevronLeft className="h-5 w-5" />
        </Button>
        <div className="flex items-center gap-3">
          <div className="text-center">
            <p className="font-bold text-base leading-none">
              {week ? `${prettyDate(week.start)} — ${prettyDate(week.end)}` : "…"}
            </p>
          </div>
          <Input
            type="date"
            value={anchor}
            onChange={(e) => e.target.value && setAnchor(e.target.value)}
            className="w-40"
            data-testid="teasing-date-input"
          />
          <Button variant="outline" size="sm" onClick={() => setAnchor(todayYMD())} data-testid="teasing-thisweek-btn">
            This week
          </Button>
        </div>
        <Button variant="ghost" size="icon" onClick={() => setAnchor(addDays(anchor, 7))} data-testid="teasing-next-week">
          <ChevronRight className="h-5 w-5" />
        </Button>
      </div>

      {/* Add mare */}
      {!readOnly && (
        <div className="flex items-center gap-2 mb-4">
          <Select value={addMareId} onValueChange={setAddMareId}>
            <SelectTrigger className="w-64" data-testid="teasing-add-mare-select">
              <SelectValue placeholder="Add a mare to this week…" />
            </SelectTrigger>
            <SelectContent>
              {available.length === 0 ? (
                <div className="px-3 py-2 text-sm text-muted-foreground">All mares added</div>
              ) : (
                available.map((m) => (
                  <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
          <Button onClick={addMare} disabled={!addMareId} className="gap-2" data-testid="teasing-add-mare-btn">
            <Plus className="h-4 w-4" /> Add
          </Button>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : !week || week.rows.length === 0 ? (
        <div className="text-center py-20 bg-card border border-dashed border-border rounded-xl">
          <p className="text-lg font-semibold">No mares in this week&apos;s teasing chart</p>
          <p className="text-muted-foreground text-sm mt-1">Add a mare above to begin tracking teasing results.</p>
        </div>
      ) : (
        <div className="border border-[#C4C8D6] rounded-md bg-card overflow-x-auto equi-scroll">
          <table className="w-full text-[13px] min-w-[900px]">
            <thead>
              <tr className="bg-primary text-primary-foreground text-[11px] uppercase tracking-wide [&>th]:border-r [&>th]:border-white/15 last:[&>th]:border-r-0">
                <th className="px-3 py-3 text-left font-semibold sticky left-0 bg-primary">Mare</th>
                {week.dates.map((d, i) => (
                  <th key={d} className="px-3 py-3 text-center font-semibold whitespace-nowrap">
                    <div>{DOW_LABELS[i]}</div>
                    <div className="text-[10px] opacity-70">
                      {fromYMD(d).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                    </div>
                  </th>
                ))}
                <th className="px-3 py-3 w-12"></th>
              </tr>
            </thead>
            <tbody>
              {week.rows.map((row) => (
                <tr key={row.mare_id} className="border-b border-[#C4C8D6] hover:bg-primary/5 [&>td]:border-r [&>td]:border-[#C4C8D6] last:[&>td]:border-r-0" data-testid={`teasing-row-${row.mare_id}`}>
                  <td className="px-3 py-3 font-semibold sticky left-0 bg-card whitespace-nowrap">{row.mare_name}</td>
                  {week.dates.map((d) => {
                    const cell = row.days[d] || { result: "", notes: "" };
                    const has = cell.result || cell.notes;
                    return (
                      <td
                        key={d}
                        onClick={() => !readOnly && openCell(row, d)}
                        className={`px-2 py-2 text-center align-top hover:bg-accent/40 min-w-[90px] ${readOnly ? "" : "cursor-pointer"}`}
                        data-testid={`teasing-cell-${row.mare_id}-${d}`}
                      >
                        {has ? (
                          <div>
                            {cell.result && <div className="font-semibold text-xs">{cell.result}</div>}
                            {cell.notes && <div className="text-[10px] italic text-muted-foreground">{cell.notes}</div>}
                          </div>
                        ) : (
                          <span className="text-border">—</span>
                        )}
                      </td>
                    );
                  })}
                  <td className="px-3 py-3 text-right">
                    {!readOnly && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-muted-foreground hover:text-destructive"
                        onClick={() => removeMare(row.mare_id)}
                        data-testid={`teasing-remove-${row.mare_id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editing?.mare_name} · {editing && prettyDate(editing.date)}
            </DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-4 py-2">
              <div className="space-y-1.5">
                <Label htmlFor="t-result">Result</Label>
                <Input
                  id="t-result"
                  value={editing.result}
                  onChange={(e) => setEditing((s) => ({ ...s, result: e.target.value }))}
                  placeholder="e.g. Winking / Kicking / No response"
                  data-testid="teasing-edit-result"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="t-notes">Notes</Label>
                <Textarea
                  id="t-notes"
                  value={editing.notes}
                  onChange={(e) => setEditing((s) => ({ ...s, notes: e.target.value }))}
                  placeholder="Add notes…"
                  data-testid="teasing-edit-notes"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={saveCell} disabled={saving} data-testid="teasing-save-btn">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
